import { Text } from '@thanx/uikit/text'
import AiGeneratedBadge from 'components/AiGeneratedBadge'
import React from 'react'

// Shared "Resolved by" cell for the feedback table. Named FeedbackResolvedByCell
// (not ResolvedByCell) so it can't be silently mis-imported for the two scene-local
// ResolvedByCell files (RAI Activity / CaseDetail) whose prop shapes differ —
// consolidating the three is the deferred follow-up noted in PR #3825.
//
// context: declares its own value type rather than importing api/recoveryAi.types
//   (a presentational component shouldn't depend on one feature's API types); both
//   ActivityResolvedBy and ResolvedBy satisfy this shape structurally, so RAI
//   callers pass their values with no cast.
export type ResolvedByValue = {
  kind: 'ai' | 'operator' | null
  name: string | null
}

type Props = {
  resolvedBy: ResolvedByValue
  // The `fallback` seam is what lets three consumers share one component: the
  // feedback table passes nothing (blank cell), RAI Activity passes a
  // <StatusBadge>, CaseDetail passes an em-dash. The cell itself owns no default.
  fallback?: React.ReactNode
  // Opt-in per surface, mirroring FeedbackTable's getViewPath/getLinkState seams:
  // the TUTD Details tab and the live RAI Escalations table both want the Figma
  // subtle chip (light spearmint10 pill + trailing sparkle cluster); only
  // CaseDetail's RecentActivities (a separate ResolvedByCell, not this
  // component) keeps the legacy solid pill. Defaults to the solid appearance so
  // an existing consumer that doesn't opt in is unchanged.
  subtleResolvedBadge?: boolean
}

const FeedbackResolvedByCell = ({
  resolvedBy,
  fallback,
  subtleResolvedBadge = false,
}: Props): React.ReactElement | null => {
  const { kind, name } = resolvedBy

  if (kind === 'ai')
    return (
      <AiGeneratedBadge
        variant="resolved"
        appearance={subtleResolvedBadge ? 'subtle' : 'solid'}
      />
    )
  if (kind === 'operator' && name) {
    return <Text.BodyText4 color="grey90">{name}</Text.BodyText4>
  }
  // operator with a null name, or kind === null → the caller's fallback (or nothing)
  if (fallback != null) return <>{fallback}</>
  return null
}

export default FeedbackResolvedByCell
